import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, RefreshCw, Search } from "lucide-react";
import { Link } from "react-router-dom";
import TbsLogo from "@/components/TbsLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "foreign-consultancy": "Foreign Consultancy",
  construction: "Construction",
  "business-solutions": "Business Solutions",
  "it-cyber-security": "IT & Cyber Security",
  "ads-marketing": "Ads & Marketing",
  general: "General",
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const AdminSubmissionsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [service, setService] = useState("all");
  const [stats, setStats] = useState({ total: 0, by_service: {} });
  const [active, setActive] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (service && service !== "all") params.service = service;
      const [list, s] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { params }),
        axios.get(`${API}/admin/submissions/stats`),
      ]);
      setItems(list.data || []);
      setStats(s.data || { total: 0, by_service: {} });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = (e) => {
    e?.preventDefault?.();
    fetchItems();
  };

  const counts = useMemo(() => stats.by_service || {}, [stats]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="admin-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="admin-back-home">
              <ChevronLeft className="h-4 w-4" /> Home
            </Link>
            <span className="h-5 w-px bg-slate-200" />
            <TbsLogo className="h-9 w-auto" />
          </div>
          <div className="text-xs text-slate-500">Admin · Submissions</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
              Contact Submissions
            </div>
            <h1 className="mt-2 font-display text-3xl text-[hsl(var(--primary))]">All inquiries</h1>
            <p className="mt-1 text-sm text-slate-600">
              Total: <span className="font-semibold text-[hsl(var(--primary))]">{stats.total}</span>
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchItems}
            disabled={loading}
            data-testid="admin-refresh-button"
            className="rounded-xl"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <Card className="mt-6 p-4 md:p-5 border border-slate-200 bg-white">
          <form onSubmit={onApply} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-7">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">Search</label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="admin-submissions-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Name, email, message..."
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">Service</label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="admin-service-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {Object.keys(SERVICE_LABELS).map((k) => (
                    <SelectItem key={k} value={k}>
                      {SERVICE_LABELS[k]} ({counts[k] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Button type="submit" className="w-full h-11 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white" data-testid="admin-apply-filters">
                Apply
              </Button>
            </div>
          </form>
        </Card>

        <Card className="mt-6 border border-slate-200 bg-white overflow-hidden">
          <Table data-testid="admin-submissions-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[20%]">Date</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Locale</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-slate-500 py-10">
                    {loading ? "Loading..." : "No submissions yet"}
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`admin-row-${it.id}`}>
                  <TableCell className="text-sm text-slate-600">{formatDate(it.created_at)}</TableCell>
                  <TableCell className="font-medium text-[hsl(var(--primary))]">{it.full_name}</TableCell>
                  <TableCell className="text-sm">
                    <a href={`mailto:${it.email}`} className="text-slate-700 hover:text-[hsl(var(--primary))]">
                      {it.email}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="bg-[hsl(var(--accent))]/15 text-[hsl(var(--primary))]">
                      {SERVICE_LABELS[it.service] || it.service}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs uppercase tracking-wider text-slate-500">{it.locale}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActive(it)}
                      data-testid={`admin-view-${it.id}`}
                      className="rounded-lg"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </main>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[hsl(var(--primary))]">
              {active?.full_name}
            </DialogTitle>
            <DialogDescription className="text-xs uppercase tracking-wider text-slate-500">
              {active && formatDate(active.created_at)}
            </DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <DetailRow label="Email" value={active.email} link={`mailto:${active.email}`} />
              <DetailRow label="Phone" value={active.phone || "—"} />
              <DetailRow label="Company" value={active.company || "—"} />
              <DetailRow label="Service" value={SERVICE_LABELS[active.service] || active.service} />
              <DetailRow label="Locale" value={active.locale?.toUpperCase()} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Message</div>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-800 leading-relaxed bg-slate-50 rounded-lg p-3 border border-slate-200">
                  {active.message}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailRow = ({ label, value, link }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2">
    <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
    {link ? (
      <a href={link} className="text-sm font-medium text-[hsl(var(--primary))] hover:underline">{value}</a>
    ) : (
      <div className="text-sm font-medium text-[hsl(var(--primary))] break-all text-right">{value}</div>
    )}
  </div>
);

export default AdminSubmissionsPage;
